// lib/vector-models.ts
// The text-to-vector models of Studio Vector. QuiverAI's Arrow 2 and Arrow 2 Telos lead the
// human-preference SVG boards and are reached only on Quiver's own API (Replicate carries Arrow 1.1,
// fal none). Recraft V4.1 stays, on fal, for the one thing Arrow does not take as a parameter: brand
// and background colours.

import { normalizeHexInput } from '#lib/svg-layer';
import type { ArrowReasoningEffort } from '#types/types';

export const QUIVER_API_BASE = 'https://api.quiver.ai/v1';

export const RECRAFT_VECTOR_ENDPOINTS = {
  'recraft-v4-1-vector': 'fal-ai/recraft/v4.1/text-to-vector',
  'recraft-v4-1-vector-pro': 'fal-ai/recraft/v4.1/pro/text-to-vector',
} as const;

/** Registry id -> Quiver model id. The trace runs on Arrow 2; Telos is kept for generation. */
export const ARROW_MODELS = {
  'arrow-2': 'arrow-2',
  'arrow-2-telos': 'arrow-2-telos',
  'arrow-2-vectorize': 'arrow-2',
} as const;

export type RecraftVectorModelId = keyof typeof RECRAFT_VECTOR_ENDPOINTS;
export type ArrowModelId = keyof typeof ARROW_MODELS;
/** The text-to-vector models the user picks; the trace is not one of them. */
export type VectorModelId = RecraftVectorModelId | 'arrow-2' | 'arrow-2-telos';
export const VECTOR_MODEL_IDS: readonly VectorModelId[] = ['arrow-2', 'arrow-2-telos', 'recraft-v4-1-vector', 'recraft-v4-1-vector-pro'];
/** The models that turn a picture into vectors: Arrow 2 in a Vector project, Recraft Vectorize elsewhere. */
export const VECTOR_TRACE_MODEL_IDS = ['arrow-2-vectorize', 'recraft-vectorize'] as const;

export const isVectorModelId = (id: unknown): id is VectorModelId =>
  typeof id === 'string' && (VECTOR_MODEL_IDS as readonly string[]).includes(id);

export const isRecraftVectorModelId = (id: unknown): id is RecraftVectorModelId =>
  typeof id === 'string' && id in RECRAFT_VECTOR_ENDPOINTS;

export const isArrowModelId = (id: unknown): id is ArrowModelId =>
  typeof id === 'string' && id in ARROW_MODELS;

export const isVectorTraceModelId = (id: unknown): boolean =>
  typeof id === 'string' && (VECTOR_TRACE_MODEL_IDS as readonly string[]).includes(id);

/** The trace a project kind gets: the best one where vectors are the whole job. */
export const vectorTraceModelFor = (projectType: unknown): 'arrow-2-vectorize' | 'recraft-vectorize' =>
  projectType === 'VECTOR' ? 'arrow-2-vectorize' : 'recraft-vectorize';

/** A #rrggbb or null; the loose forms are refused rather than sent as black. */
export const normalizeVectorHex = (value: unknown): string | null =>
  typeof value === 'string' ? normalizeHexInput(value) : null;

/** fal's RGBColor from a #rrggbb. lib/canvas-utils has a parser too, but it pulls the browser workers into the route. */
export const hexToRgb = (value: unknown): { r: number; g: number; b: number } | null => {
  const hex = normalizeVectorHex(value);
  if (!hex) return null;
  const n = parseInt(hex.slice(1), 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
};

export const VECTOR_OUTPUT_MIN_SIDE = 256;
export const VECTOR_OUTPUT_MAX_SIDE = 4096;

/** The artboard decides the output geometry: its size is sent, scaled as a whole so the ratio holds. */
export const resolveVectorImageSize = (width: unknown, height: unknown): { width: number; height: number } => {
  const w = typeof width === 'number' && Number.isFinite(width) && width > 0 ? width : 1024;
  const h = typeof height === 'number' && Number.isFinite(height) && height > 0 ? height : 1024;
  let scale = Math.min(1, VECTOR_OUTPUT_MAX_SIDE / Math.max(w, h));
  if (Math.min(w, h) * scale < VECTOR_OUTPUT_MIN_SIDE) scale = Math.min(VECTOR_OUTPUT_MIN_SIDE / Math.min(w, h), VECTOR_OUTPUT_MAX_SIDE / Math.max(w, h));
  return { width: Math.round(w * scale), height: Math.round(h * scale) };
};

/** The largest rect of the given aspect that fits inside `outer`, centred: an output keeps its shape on the artboard. */
export const fitRectInside = (outer: { x: number; y: number; w: number; h: number }, aspect: number): { x: number; y: number; w: number; h: number } => {
  if (!(aspect > 0) || !(outer.w > 0) || !(outer.h > 0)) return { ...outer };
  const w = Math.min(outer.w, outer.h * aspect);
  const h = w / aspect;
  return { x: outer.x + (outer.w - w) / 2, y: outer.y + (outer.h - h) / 2, w, h };
};

export const ARROW_REASONING_EFFORTS: readonly ArrowReasoningEffort[] = ['low', 'medium', 'high', 'xhigh'];

/** Bounds the spend of one call: the reservation is computed from it, the settlement from what was used. */
export const ARROW_MAX_OUTPUT_TOKENS = 32_768;
const ARROW_MAX_INPUT_TOKENS_RESERVED = 16_000;

/** Quiver's list prices, USD per million tokens. */
export const ARROW_TOKEN_PRICES_USD: Record<'arrow-2' | 'arrow-2-telos', { input: number; output: number }> = {
  'arrow-2': { input: 4, output: 20 },
  'arrow-2-telos': { input: 6, output: 30 },
};

// Credits are worth 1/5 USD (lib/llmClient USD_TO_CREDITS_RATIO). The margin is provisional until
// the owner sets the grid; it lives here, beside the prices it multiplies.
const CREDITS_PER_USD = 5;
export const ARROW_CREDIT_MARGIN = 3;

const arrowPriceFor = (modelId: ArrowModelId) => ARROW_TOKEN_PRICES_USD[ARROW_MODELS[modelId]];

/** Credits for a call that used these tokens, rounded up to a tenth. */
export const arrowCreditsForUsage = (modelId: ArrowModelId, usage: { input_tokens?: unknown; output_tokens?: unknown } | null | undefined): number => {
  const count = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : 0);
  const price = arrowPriceFor(modelId);
  const usd = (count(usage?.input_tokens) * price.input + count(usage?.output_tokens) * price.output) / 1_000_000;
  // The epsilon keeps a float like 12.000000000000002 from rounding a whole tenth up.
  return usd > 0 ? Math.ceil(usd * CREDITS_PER_USD * ARROW_CREDIT_MARGIN * 10 - 1e-9) / 10 : 0;
};

/**
 * What to settle a call at, or null to keep the reservation: a response without token counts must
 * never become a free call.
 */
export const arrowSettlementCredits = (modelId: ArrowModelId, usage: { input_tokens?: unknown; output_tokens?: unknown } | null | undefined): number | null => {
  const reported = [usage?.input_tokens, usage?.output_tokens].some((v) => typeof v === 'number' && Number.isFinite(v) && v > 0);
  return reported ? Math.min(arrowCreditsForUsage(modelId, usage), arrowReservedCredits(modelId)) : null;
};

/** The reservation: the most one call can cost under the output-token bound. */
export const arrowReservedCredits = (modelId: ArrowModelId): number =>
  arrowCreditsForUsage(modelId, { input_tokens: ARROW_MAX_INPUT_TOKENS_RESERVED, output_tokens: ARROW_MAX_OUTPUT_TOKENS });

/** Brand colours become an instruction: Arrow takes no palette parameter. */
export const buildArrowInstructions = (style: unknown, colors: unknown): string | undefined => {
  const parts: string[] = [];
  if (typeof style === 'string' && style.trim()) parts.push(style.trim());
  const palette = (Array.isArray(colors) ? colors : []).map(normalizeVectorHex).filter((hex): hex is string => hex !== null);
  if (palette.length) parts.push(`Use only these colours: ${palette.join(', ')}.`);
  return parts.length ? parts.join(' ') : undefined;
};

// Bounds the input side of the reservation: a prompt and its style past these lengths are cut, and a
// traced picture is resized by Quiver to this side before it is read.
const ARROW_PROMPT_MAX_CHARS = 4_000;
const ARROW_TRACE_TARGET_SIZE = 2_048;

export const buildArrowGenerationBody = (input: {
  modelId: 'arrow-2' | 'arrow-2-telos';
  prompt: string;
  style?: unknown;
  colors?: unknown;
  reasoningEffort?: unknown;
  width: unknown;
  height: unknown;
}): Record<string, unknown> => {
  const size = resolveVectorImageSize(input.width, input.height);
  const instructions = buildArrowInstructions(input.style, input.colors)?.slice(0, ARROW_PROMPT_MAX_CHARS);
  const effort = ARROW_REASONING_EFFORTS.includes(input.reasoningEffort as ArrowReasoningEffort) ? input.reasoningEffort : undefined;
  return {
    model: ARROW_MODELS[input.modelId],
    prompt: input.prompt.slice(0, ARROW_PROMPT_MAX_CHARS),
    n: 1,
    max_output_tokens: ARROW_MAX_OUTPUT_TOKENS,
    viewBox: { minX: 0, minY: 0, width: size.width, height: size.height },
    ...(instructions ? { instructions } : {}),
    ...(effort ? { reasoning_effort: effort } : {}),
  };
};

/** Quiver takes an http(s) URL or the bare base64 of the picture; a data URL is sent as the latter. */
export const buildArrowVectorizationBody = (imageSource: string): Record<string, unknown> => {
  const dataUrl = /^data:[^;,]+;base64,(.+)$/s.exec(imageSource);
  return {
    model: ARROW_MODELS['arrow-2-vectorize'],
    image: dataUrl ? { base64: dataUrl[1] } : { url: imageSource },
    target_size: ARROW_TRACE_TARGET_SIZE,
    max_output_tokens: ARROW_MAX_OUTPUT_TOKENS,
  };
};

/** The SVG markup and the usage of a Quiver response; anything else is refused. */
export const readArrowResponse = (json: unknown): { svg: string; usage: { input_tokens?: number; output_tokens?: number } } => {
  const body = json as { data?: Array<{ svg?: unknown }>; usage?: { input_tokens?: number; output_tokens?: number } } | null;
  // Only the document itself is kept: any prose or fence around it would make the file unreadable.
  const svg = typeof body?.data?.[0]?.svg === 'string' ? /<svg\b[\s\S]*<\/svg>/i.exec(body.data[0].svg)?.[0] : undefined;
  if (!svg) throw new Error('The vector model returned no SVG output.');
  return { svg, usage: body?.usage ?? {} };
};
